import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { RefreshCw } from "lucide-react";
import { BtcCard } from "@/components/dash/BtcCard";
import { MomentumCard } from "@/components/dash/MomentumCard";
import { MarketsSection } from "@/components/dash/MarketsSection";
import { ReadingsLog } from "@/components/dash/ReadingsLog";
import { Glossary } from "@/components/dash/Glossary";
import BotPage from "@/components/bot/BotPage";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REFRESH = 60;

export default function PolymarketPage() {
  const [btc, setBtc] = useState(null);
  const [readings, setReadings] = useState([]);
  const [countdown, setCountdown] = useState(REFRESH);
  const [loading, setLoading] = useState(false);
  const [tick, setTick] = useState(0);
  const [tab, setTab] = useState("bot");

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/btc`, { params: { log: true } });
      setBtc(data);
      const r = await axios.get(`${API}/readings`);
      setReadings(r.data);
    } catch (e) {
      toast.error("Couldn't reach the price feed. Will retry next refresh.");
    } finally {
      setLoading(false);
      setCountdown(REFRESH);
      setTick((t) => t + 1);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    const id = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) { refresh(); return REFRESH; }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [refresh]);

  const clearLog = async () => {
    await axios.delete(`${API}/readings`);
    setReadings([]);
    toast.success("Log cleared");
  };

  return (
    <div data-testid="polymarket-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <h1 className="font-head font-bold text-3xl tracking-tight">BTC 15-minute</h1>
          <p className="text-sm text-slate-400">Polymarket US Bitcoin Up/Down analytics</p>
        </div>
        <div className="flex gap-1 p-1 rounded-full glass" data-testid="main-tabs">
          {[["bot", "Signal"], ["dash", "Markets & log"]].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${tab === k ? "bg-[var(--cyan)] text-[#03141A]" : "text-slate-400 hover:text-slate-200"}`}>{l}</button>
          ))}
        </div>
      </div>
      {tab === "bot" ? <BotPage /> : (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-6">
          <div className="lg:col-span-12 flex items-center justify-end gap-2 text-xs text-slate-400 font-num">
            next update {countdown}s
            <button onClick={refresh} disabled={loading} data-testid="manual-refresh-btn" className="h-8 w-8 rounded-full glass flex items-center justify-center" aria-label="Refresh"><RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /></button>
          </div>
          <div className="lg:col-span-12 min-w-0"><Glossary /></div>
          <div className="lg:col-span-7 min-w-0"><BtcCard btc={btc} /></div>
          <div className="lg:col-span-5 min-w-0"><MomentumCard btc={btc} /></div>
          <div className="lg:col-span-12 min-w-0"><MarketsSection tick={tick} /></div>
          <div className="lg:col-span-12 min-w-0"><ReadingsLog readings={readings} onClear={clearLog} /></div>
        </div>
      )}
    </div>
  );
}
